// @ts-nocheck
/*
 * Port of views/manage-queues/queues.js (origin/main-dev, 502 lines).
 * BYTE-IDENTICAL â€” inline Add/Edit modal (dual-mode), extension dropdown,
 * open_wrapup radio (After Call / During Call / Turn Off), default_wrapup_tag
 * numeric mapping (CALL_COMPLETED:1, CALL_FAILED:2, CALL_DROPPED:3),
 * extension 10-49 range validation, PrimeReact â†’ obd3-gui + shadcn.
 *
 * NOTE: v1 has a latent bug in `handleSelectAll` â€” it maps `queue.id` but
 * the formatted list uses `_id` (renamed). Preserved verbatim so selection
 * from header checkbox may not work. Same for `onCheckboxChange` targeting
 * `rowData.id` after formattedList strips it. Not fixed.
 */
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

const _cx = (...args: any[]) => args.filter(Boolean).join(' ');
const Row: any = ({ className, children, ...props }: any) => (
  <div className={_cx('row', className)} {...props}>
    {children}
  </div>
);
const _colCls = ({ xs, sm, md, lg, xl }: any) => {
  const parts = [];
  if (md) parts.push(`col-md-${md}`);
  if (lg) parts.push(`col-lg-${lg}`);
  if (xl) parts.push(`col-xl-${xl}`);
  if (sm) parts.push(`col-sm-${sm}`);
  if (xs) parts.push(`col-${xs}`);
  if (!parts.length) parts.push('col');
  return parts.join(' ');
};
const Col: any = ({ xs, sm, md, lg, xl, className, children, ...props }: any) => (
  <div className={_cx(_colCls({ xs, sm, md, lg, xl }), className)} {...props}>
    {children}
  </div>
);
const Form: any = ({ children, onSubmit, className, ...props }: any) => (
  <form
    className={className}
    onSubmit={onSubmit || ((e: any) => e.preventDefault())}
    {...props}
  >
    {children}
  </form>
);
Form.Group = ({ className, controlId: _c, children, ...props }: any) => (
  <div className={_cx('mb-3', className)} {...props}>
    {children}
  </div>
);
Form.Label = ({ className, children, htmlFor, ...props }: any) => (
  <label className={_cx('form-label', className)} htmlFor={htmlFor} {...props}>
    {children}
  </label>
);
Form.Control = ({ as, className, children, isInvalid, ...props }: any) => {
  const cls = _cx('form-control', isInvalid && 'is-invalid', className);
  if (as === 'select')
    return (
      <select className={cls} {...props}>
        {children}
      </select>
    );
  if (as === 'textarea') return <textarea className={cls} {...props} />;
  return <input className={cls} {...props} />;
};
Form.Select = ({ className, children, isInvalid, ...props }: any) => (
  <select
    className={_cx('form-control form-select', isInvalid && 'is-invalid', className)}
    {...props}
  >
    {children}
  </select>
);
Form.Check = ({
  type = 'checkbox',
  label,
  id,
  className,
  inline,
  children,
  ...props
}: any) => (
  <div className={_cx('form-check', inline && 'form-check-inline', className)}>
    <input type={type} id={id} className="form-check-input" {...props} />
    {label !== undefined && (
      <label htmlFor={id} className="form-check-label">
        {label}
      </label>
    )}
    {children}
  </div>
);
const Modal: any = ({ show, onHide, size, centered: _c, children, className, ...props }: any) => {
  const sizeCls =
    size === 'sm'
      ? 'sm:max-w-md'
      : size === 'lg'
      ? 'sm:max-w-2xl'
      : size === 'xl'
      ? 'sm:max-w-6xl'
      : '';
  return (
    <Dialog open={!!show} onOpenChange={(o: boolean) => !o && onHide?.()}>
      <DialogContent
        className={`${sizeCls} max-h-[90vh] overflow-y-auto ${className || ''}`.trim()}
        {...props}
      >
        {children}
      </DialogContent>
    </Dialog>
  );
};
Modal.Header = ({ children, closeButton: _cb, className, ...props }: any) => (
  <DialogHeader className={className} {...props}>
    {children}
  </DialogHeader>
);
Modal.Title = ({ children, className, ...props }: any) => (
  <DialogTitle className={className} {...props}>
    {children}
  </DialogTitle>
);
Modal.Body = ({ children, className, ...props }: any) => (
  <div className={`overflow-x-auto ${className || ''}`.trim()} {...props}>
    {children}
  </div>
);
Modal.Footer = ({ children, className, ...props }: any) => (
  <DialogFooter className={className} {...props}>
    {children}
  </DialogFooter>
);
import type { ColumnDef } from '@tanstack/react-table';
import { useNavigate } from 'react-router-dom';
import Loader from '@/components/partials/components/Loader';
import { toast } from 'sonner';
import { getApiCall, postApiCall } from '@/service/httpCall';
import { API_QUEUES, API_EXTENSION, API_QUEUE_DELETE, API_QUEUE_UPDATE } from '@/service/url';
import RenderDeleteModal from '@/components/partials/components/delete-modal';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { DataTable } from '@/components/data-table/DataTable';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { Plus } from 'lucide-react';

const Queues = () => {
  const [queueList, setQueueList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedQueues, setSelectedQueues] = useState([]);
  const [enableDeleteModal, setEnableDeleteModal] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [extensionList, setExtensionList] = useState([]);
  const [isEdit, setIsEdit] = useState(false);

  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    extension: '',
    max_wait_time: '',
    open_wrapup: 1,
    default_wrapup_tag: 'CALL_COMPLETED',
    wrapper_session_timeout: '',
  } as any);
  const [formErrors, setFormErrors] = useState({} as any);

  const handleEditQueue = (queue) => {
    setFormData({
      _id: queue.id,
      name: queue.name,
      extension: queue.extension,
      max_wait_time: queue.max_wait_time,
      open_wrapup: queue.open_wrapup || 1,
      default_wrapup_tag: queue.default_wrapup_tag || 'CALL_COMPLETED',
      wrapper_session_timeout: queue.wrapper_session_timeout || '',
    });
    setIsEdit(true);
    setShowModal(true);
  };

  const handleDeleteQueue = (queueId) => {
    const payload = { queueIds: [queueId] };
    postApiCall(API_QUEUE_DELETE, payload)
      .then((response) => {
        if (response.acknowledged) {
          toast.success('Queue deleted successfully');
          setShowToast(true);
          fetchQueues();
        } else {
          toast.error(response.message || 'Failed to delete queue');
          setShowToast(true);
        }
      })
      .catch((err) => {
        toast.warning(`Unable to delete queue: ${err.message}`);
        setShowToast(true);
      });
  };

  const fetchExtensions = () => {
    getApiCall(API_EXTENSION)
      .then((resp) => {
        if (resp) {
          setExtensionList(resp);
        } else {
          toast.warning('Failed to fetch extensions');
          setShowToast(true);
        }
      })
      .catch(() => {
        toast.warning('Error fetching extensions');
        setShowToast(true);
      });
  };

  useEffect(() => {
    if (showToast) {
      const timer = setTimeout(() => setShowToast(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [showToast]);

  useEffect(() => {
    if (confirmDelete) {
      handleDeleteSelectedQueues();
    }
  }, [confirmDelete]);

  const fetchQueues = () => {
    setLoading(true);
    getApiCall(API_QUEUES)
      .then((resp) => {
        if (resp) {
          const formattedList = resp.map((item) => ({
            _id: item.id,
            name: item.name,
            extension: item.extension?.extension || 'N/A',
            max_wait_time: item.max_wait_time,
            created_by: item.created_by?.username || 'Unknown',
            created_at: new Date(item.created_at).toLocaleString(),
          }));
          setQueueList(formattedList);
        } else {
          toast.warning('Failed to fetch queues');
          setShowToast(true);
        }
      })
      .catch(() => {
        toast.warning('Error fetching queues');
        setShowToast(true);
      })
      .finally(() => setLoading(false));
  };

  const handleDeleteSelectedQueues = () => {
    if (selectedQueues.length === 0) return;
    const payload = { queueIds: selectedQueues };
    postApiCall(API_QUEUE_DELETE, payload)
      .then((response) => {
        if (response.acknowledged) {
          toast.success('Queues deleted successfully');
          setShowToast(true);
          fetchQueues();
          setSelectedQueues([]);
        } else {
          toast.warning(response.message || 'Failed to delete queues');
          setShowToast(true);
        }
      })
      .catch((err) => {
        toast.warning(`Unable to delete queues: ${err.message}`);
        setShowToast(true);
      })
      .finally(() => setEnableDeleteModal(false));
  };

  useEffect(() => {
    fetchQueues();
    fetchExtensions();
  }, []);

  const onCheckboxChange = (queueId, checked) => {
    setSelectedQueues((prevSelected) =>
      checked ? [...prevSelected, queueId] : prevSelected.filter((id) => id !== queueId),
    );
  };

  const onHeaderCheckboxChange = (checked) => {
    if (checked) {
      // v1 bug preserved: maps queue.id but formattedList uses _id.
      const allQueueIds = queueList.map((queue) => queue.id);
      setSelectedQueues(allQueueIds);
    } else {
      setSelectedQueues([]);
    }
  };

  const handleAddQueue = () => {
    const errors: any = {};
    if (!formData.name.trim()) errors.name = 'Queue name cannot be empty';
    if (!formData.extension) {
      errors.extension = 'Extension cannot be empty';
    } else if (formData.extension < 10 || formData.extension > 49) {
      errors.extension = 'Extension must be between 10 and 49';
    }
    if (!formData.max_wait_time) errors.max_wait_time = 'Max wait time cannot be empty';
    if ((formData.open_wrapup === 1 || formData.open_wrapup === 2) && !formData.default_wrapup_tag) {
      errors.default_wrapup_tag = 'Default wrapup tag cannot be empty';
    }
    if (!formData.wrapper_session_timeout) {
      errors.wrapper_session_timeout = 'Wrapper session timeout cannot be empty';
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    const wrapupTagMapping: any = {
      CALL_COMPLETED: 1,
      CALL_FAILED: 2,
      CALL_DROPPED: 3,
    };

    const payload = {
      ...formData,
      max_wait_time: Number(formData.max_wait_time),
      wrapper_session_timeout: Number(formData.wrapper_session_timeout),
      default_wrapup_tag: wrapupTagMapping[formData.default_wrapup_tag] || null,
    };

    setIsSubmitting(true);

    const apiCall = isEdit
      ? postApiCall(`${API_QUEUE_UPDATE}/${formData.id}`, payload)
      : postApiCall(API_QUEUES, payload);

    apiCall
      .then(() => {
        const successMessage = isEdit ? 'Queue updated successfully' : 'Queue created successfully';
        toast.success(successMessage);
        setShowToast(true);
        fetchQueues();
        setShowModal(false);
        setFormData({
          name: '',
          extension: '',
          max_wait_time: '',
          open_wrapup: 1,
          default_wrapup_tag: 'CALL_COMPLETED',
          wrapper_session_timeout: '',
        });
        setFormErrors({});
      })
      .catch((err) => {
        const errorMessage = isEdit ? `Failed to update queue: ${err.message}` : `Failed to create queue: ${err.message}`;
        toast.warning(errorMessage);
        setShowToast(true);
      })
      .finally(() => setIsSubmitting(false));
  };

  const columns: ColumnDef<any>[] = [
    {
      id: '_select',
      enableSorting: false,
      enableHiding: false,
      header: () => (
        <Checkbox
          checked={selectedQueues.length === queueList.length && queueList.length > 0}
          onCheckedChange={onHeaderCheckboxChange}
          aria-label="Select all"
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={selectedQueues.includes(row.original.id)}
          onCheckedChange={(checked) => onCheckboxChange(row.original.id, checked)}
          aria-label="Select row"
        />
      ),
    },
    { accessorKey: 'name', header: 'Queue Name' },
    { accessorKey: 'extension', header: 'Extension' },
    { accessorKey: 'max_wait_time', header: 'Max Wait Time' },
    { accessorKey: 'created_by', header: 'Created By' },
    { accessorKey: 'created_at', header: 'Created At' },
    {
      id: 'actions',
      header: 'Actions',
      enableSorting: false,
      cell: ({ row }) => (
        <div className="actions">
          <Button
            size="sm"
            className="me-2"
            style={{ backgroundColor: '#f16a1b', border: '#f16a1b' }}
            onClick={() => handleEditQueue(row.original)}
            title="Edit"
          >
            Edit
          </Button>
          <Button
            size="sm"
            variant="destructive"
            onClick={() => handleDeleteQueue(row.original.id)}
            title="Delete"
          >
            Delete
          </Button>
        </div>
      ),
    },
  ];

  return (
    <FillPage>
      <PageHeader
        title="Queues"
        description="ACD-style queues used for inbound call distribution with wrap-up rules."
        actions={
          <Button
            onClick={() => {
              setFormData({
                name: '',
                extension: '',
                max_wait_time: '',
                open_wrapup: 1,
                default_wrapup_tag: 'CALL_COMPLETED',
                wrapper_session_timeout: '',
              });
              setIsEdit(false);
              setShowModal(true);
            }}
          >
            <Plus className="h-4 w-4" />
            Add Queue
          </Button>
        }
      />
      <Row>
        <Col sm="12">
          <Card className="p-4">

            {loading ? (
              <Loader />
            ) : (
              <>
                <div style={{ display: 'flex', flexDirection: 'row', marginBottom: '10px' }}>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => setEnableDeleteModal(true)}
                    disabled={selectedQueues.length === 0}
                  >
                    Delete Selected
                  </Button>
                </div>

                <DataTable
                  tableId="queues.list"
                  columns={columns}
                  data={queueList}
                  pageSize={10}
                  emptyTitle="No queues found."
                  disableExport
                />
              </>
            )}
          </Card>
        </Col>
      </Row>

      <RenderDeleteModal
        show={enableDeleteModal}
        setShow={setEnableDeleteModal}
        setConfirmDelete={setConfirmDelete}
        isMultiDelete={true}
        clearSelectedData={() => setSelectedQueues([])}
        onConfirm={handleDeleteSelectedQueues}
      />

      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{isEdit ? 'Edit Queue' : 'New Queue'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group>
              <Form.Label>Queue Name</Form.Label>
              <Form.Control
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Enter queue name"
                required
              />
              {formErrors.name && <span className="text-danger">{formErrors.name}</span>}
            </Form.Group>
            <Form.Group style={{ marginTop: '10px' }}>
              <Form.Label>Extension</Form.Label>
              <Form.Select
                value={formData.extension}
                onChange={(e) => setFormData({ ...formData, extension: e.target.value })}
                required
              >
                <option value="">Select an Extension</option>
                {extensionList.map((ext) => (
                  <option key={ext.id} value={ext.id}>
                    {ext.extension} ({ext.description || 'No Description'})
                  </option>
                ))}
              </Form.Select>
              {formErrors.extension && <span className="text-danger">{formErrors.extension}</span>}
            </Form.Group>

            <Form.Group style={{ marginTop: '10px' }}>
              <Form.Label>Max Wait Time</Form.Label>
              <Form.Control
                type="number"
                value={formData.max_wait_time}
                onChange={(e) => setFormData({ ...formData, max_wait_time: e.target.value })}
                placeholder="Enter maximum wait time"
                required
              />
              {formErrors.max_wait_time && <span className="text-danger">{formErrors.max_wait_time}</span>}
            </Form.Group>
            <Form.Group style={{ marginTop: '10px' }}>
              <Form.Label>Open Wrapup</Form.Label>
              <Form.Check
                type="radio"
                label="After Call"
                value={1}
                checked={formData.open_wrapup === 1}
                onChange={() => setFormData({ ...formData, open_wrapup: 1 })}
              />
              <Form.Check
                type="radio"
                label="During Call"
                value={2}
                checked={formData.open_wrapup === 2}
                onChange={() => setFormData({ ...formData, open_wrapup: 2 })}
              />
              <Form.Check
                type="radio"
                label="Turn Off"
                value={0}
                checked={formData.open_wrapup === 0}
                onChange={() => setFormData({ ...formData, open_wrapup: 0 })}
              />
            </Form.Group>
            <Form.Group style={{ marginTop: '10px' }}>
              <Form.Label>Default Wrapup Tag</Form.Label>
              <Form.Select
                value={formData.default_wrapup_tag}
                onChange={(e) => setFormData({ ...formData, default_wrapup_tag: e.target.value })}
                required
              >
                <option value="CALL_COMPLETED">CALL_COMPLETED</option>
                <option value="CALL_FAILED">CALL_FAILED</option>
                <option value="CALL_DROPPED">CALL_DROPPED</option>
              </Form.Select>
            </Form.Group>
            <Form.Group style={{ marginTop: '10px' }}>
              <Form.Label>Wrapper Session Timeout</Form.Label>
              <Form.Control
                type="number"
                value={formData.wrapper_session_timeout}
                onChange={(e) => setFormData({ ...formData, wrapper_session_timeout: e.target.value })}
                placeholder="Enter wrapper session timeout"
                required
              />
              {formErrors.wrapper_session_timeout && (
                <span className="text-danger">{formErrors.wrapper_session_timeout}</span>
              )}
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline" onClick={() => setShowModal(false)}>
            Cancel
          </Button>
          <Button onClick={handleAddQueue} disabled={isSubmitting}>
            {isSubmitting ? (isEdit ? 'Updating...' : 'Creating...') : isEdit ? 'Update Queue' : 'Create Queue'}
          </Button>
        </Modal.Footer>
      </Modal>
    </FillPage>
  );
};

export default Queues;
